import React from 'react';
import {
  ListItem,
  Grid,
  Button,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Game, LotteryEntry } from '../types/types';

interface GameItemProps {
  game: Game;
  loading: boolean;
  disableButtons: boolean;
  isReserved: boolean;
  isPending: boolean;
  isOwnPending: boolean;
  disableReserve: boolean;
  bookingPeriodOpen: boolean;
  tokenFound: boolean;
  publicName?: string | undefined;
  onReserve: (gameId: number, day: string) => void;
  onCancelInitial: (gameId: number) => void;
  onCancelConfirmed: (gameId: number) => void;
  onSetWinner?: (entryId: string) => void;
  onCancelLotteryEntry?: (entryId: string) => void;
  onToggleDisableGame?: (gameId: number, disabled: boolean) => void;
  lotteryEntries?: LotteryEntry[];
  mode?: 'LOTTERY' | 'FCFS';
  isAdmin?: boolean;
  isGameDisabled?: boolean;
}

const GameItem: React.FC<GameItemProps> = ({
  game,
  loading,
  disableButtons,
  isReserved,
  isPending,
  disableReserve,
  tokenFound,
  bookingPeriodOpen,
  publicName,
  isOwnPending,
  onReserve,
  onCancelInitial,
  onCancelConfirmed,
  onSetWinner,
  onCancelLotteryEntry,
  onToggleDisableGame,
  lotteryEntries,
  mode = 'FCFS',
  isAdmin = false,
  isGameDisabled = false,
}) => {
  const gameTime = new Date(game.start).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const isLotteryMode = mode === 'LOTTERY';
  const isFCFSMode = mode === 'FCFS';

  return (
    <ListItem style={{ padding: '8px 0px', display: 'flex', alignItems: 'center', maxWidth: '800px', margin: '0 auto' }}>
      <Grid container spacing={1} alignItems="center" wrap="nowrap">
        {/* Kellonaika */}
        <Grid
          item
          style={{
            flex: '0 0 auto',
            marginRight: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="span"
            style={{
              fontSize: '0.75rem',
              color: 'gray',
              textAlign: 'center',
            }}
          >
            {gameTime}
          </Box>
        </Grid>

        {/* Admin: disable/enable game toggle */}
        {isAdmin && onToggleDisableGame && (
          <Grid item style={{ flex: '0 0 auto', marginRight: '4px' }}>
            <IconButton
              size="small"
              color={isGameDisabled ? 'success' : 'error'}
              onClick={() => onToggleDisableGame(game.id, !isGameDisabled)}
              title={isGameDisabled ? 'Vapauta varattavaksi' : 'Estä varaaminen'}
            >
              {isGameDisabled ? <CheckCircleOutlineIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
            </IconButton>
          </Grid>
        )}

        {/* Joukkueet */}
        <Grid item style={{ flex: '1 1 auto', overflow: 'hidden', marginRight: '0px' }}>
          <Typography
            variant="body1"
            style={{
              wordWrap: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              ...(isGameDisabled ? { textDecoration: 'line-through', color: 'gray' } : {}),
            }}
          >
            {game.homeTeamName} – {game.awayTeamName}
          </Typography>
        </Grid>

        {isLotteryMode && lotteryEntries && lotteryEntries.length > 0 && (
          <Box sx={{ mt: 0.5, mb: 0.5 }}>
            {lotteryEntries
              .filter(entry => entry.status !== 'CANCELLED')
              .map((entry, index) => (
                <Box 
                  key={entry.id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    background: index % 2 === 0 ? '#dfdfdf' : 'white',
                    padding: '2px 6px',
                    marginBottom: '1px',
                    gap: 0.5
                  }}
                >
                  <Typography 
                    sx={{ 
                      fontSize: '0.8rem',
                      color: entry.status === 'WINNER' ? 'red' : 'inherit',
                      fontWeight: entry.status === 'WINNER' ? 'bold' : 'normal',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {entry.person_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {isAdmin && entry.status !== 'WINNER' && onSetWinner && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        onClick={() => onSetWinner(entry.id)}
                        sx={{ fontSize: '0.65rem', padding: '1px 4px', minWidth: 'auto', whiteSpace: 'nowrap' }}
                      >
                        Aseta voittajaksi
                      </Button>
                    )}
                    {isAdmin && entry.status !== 'WINNER' && onCancelLotteryEntry && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => onCancelLotteryEntry(entry.id)}
                        sx={{ fontSize: '0.65rem', padding: '1px 4px', minWidth: 'auto', whiteSpace: 'nowrap' }}
                      >
                        Peru osallistuminen
                      </Button>
                    )}
                  </Box>
                </Box>
              ))
            }
          </Box>
        )}

        {/* Lottery */}
        {isLotteryMode && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size='small'
              onClick={() => onReserve(game.id, game.date)}
              disabled={disableButtons || loading || isGameDisabled}
              style={{ whiteSpace: 'nowrap' }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Arvonta'}
            </Button>
            {tokenFound && !isAdmin && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={!bookingPeriodOpen}
                onClick={() => onCancelConfirmed(game.id)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {'Peruuta'}
              </Button>
            )}
          </Box>
        )}

        {/* Saatavilla oleva peli */}
        {isFCFSMode && !isReserved && !isPending && !disableReserve && (
          <Grid item style={{ flex: '0 0 auto', textAlign: 'right', display: 'flex', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onReserve(game.id, game.date)}
              disabled={disableButtons || loading || isGameDisabled}
              style={{ whiteSpace: 'nowrap' }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Varaa'}
            </Button>
          </Grid>
        )}

        {/* Oma varaus käynnissä */}
        {isFCFSMode && isOwnPending && tokenFound && (
          <Grid item style={{ flex: '0 0 auto', textAlign: 'right', display: 'flex', alignItems: 'center' }}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Typography color="text.main" style={{ fontSize: '0.875rem', marginRight: '8px' }}>
                Oma <br /> varauksesi <br /> käynnissä
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => onCancelInitial(game.id)}
                style={{ whiteSpace: 'nowrap' }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Peruuta'}
              </Button>
            </Box>
          </Grid>
        )}


        {/* Jonkun muun varaus käynnissä */}
        {isFCFSMode && isPending && !isOwnPending && !tokenFound && (
          <Grid item style={{ flex: '0 0 auto', textAlign: 'right', display: 'flex', alignItems: 'center' }}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Typography color="text.main" style={{ fontSize: '0.875rem', marginRight: '8px' }}>
                Jonkun muun <br /> varaus käynnissä
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Varattu peli */}
        {isFCFSMode && isReserved && (
          <Grid item style={{ flex: '0 0 auto', textAlign: 'right', display: 'flex', alignItems: 'center' }}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Typography color="text.main" style={{ fontSize: '0.875rem', marginRight: '8px' }}>
                {publicName ? publicName : '(nimi piilotettu)'}
              </Typography>
              {(tokenFound || isAdmin) && (
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={!bookingPeriodOpen && !isAdmin}
                  onClick={() => onCancelConfirmed(game.id)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Peruuta'}
                </Button>
              )}
            </Box>
          </Grid>
        )}

        {/* Ei saatavilla oleva peli */}
        {isFCFSMode && ((isOwnPending && !tokenFound) || (!isReserved && disableReserve)) && (
          <Grid item style={{ flex: '0 0 auto', textAlign: 'right', display: 'flex', alignItems: 'center' }}>
            <Typography color="text.main" style={{ fontSize: '0.875rem' }}>
              Ei varattavissa
            </Typography>
          </Grid>
        )}
      </Grid>
    </ListItem>
  );
};

export default GameItem;
